import {
  Feature,
  LineString,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from 'geojson';
import {
  bearing,
  booleanPointInPolygon,
  destination,
  distance,
  lineIntersect,
  lineString,
  point,
} from '@turf/turf';
import { sortBy } from 'lodash';
import { unitsDegrees } from '../units';

type LineSegment = [Position, Position];

/*
 * Given a set of (collinear) points, determine the distance from the given
 * point to each point. Return as a list of pairs: [distanceFromPoint, point].
 */
export const getSortedDistancePairsFromPoint = (
  points: Feature<Point>[],
  pt: Feature<Point>,
): [number, Feature<Point>][] =>
  sortBy(
    points.map((p) => [distance(p, pt), p]),
    ([dist]) => dist,
  );

/*
 * Split the given line at each coordinate in its geometry. Return a list of
 * individual lines, each consisting of exactly two coordinates.
 */
const splitLineIntoSegments = (line: Feature<LineString>) => {
  const { coordinates } = line.geometry;
  if (coordinates.length < 2) return [];
  return [...Array(coordinates.length - 1).keys()].map(
    (i) => [coordinates[i], coordinates[i + 1]] as LineSegment,
  );
};

/*
 * Determine whether the given lines intersect inside the given polygon.
 */
export const linesIntersectInsidePolygon = (
  line1: Feature<LineString>,
  line2: Feature<LineString>,
  polygon: Feature<Polygon | MultiPolygon>,
) => {
  const intersections = lineIntersect(line1, line2);
  return intersections.features.some((intersection) =>
    booleanPointInPolygon(intersection, polygon),
  );
};

/*
 * Determine whether the given line intersects itself inside the given polygon.
 */
export const lineIntersectsItselfInsidePolygon = (
  feature: Feature<LineString>,
  polygon: Feature<Polygon | MultiPolygon>,
) => {
  const lineSegments = splitLineIntoSegments(feature).map((segment) =>
    lineString(segment),
  );
  for (let i = 0; i < lineSegments.length; i += 1) {
    // Line segment cannot intersect itself, nor next adjacent segment (unless
    // collinear, which is unlikely), so loop from i + 2 to the end
    for (let j = i + 2; j < lineSegments.length; j += 1) {
      const intersectionInPolygon = linesIntersectInsidePolygon(
        lineSegments[i],
        lineSegments[j],
        polygon,
      );
      if (intersectionInPolygon) return true;
    }
  }
  return false;
};

/*
 * Extend the given row at each end by the given distance (in coordinate
 * degrees), preserving the bearing of the first and last line segments.
 */
export const extendLine = (
  line: Feature<LineString>,
  distanceDegrees: number,
) => {
  const { coordinates } = line.geometry;
  if (coordinates.length < 2) return line;
  const firstBearing = bearing(coordinates[1], coordinates[0]);
  const lastBearing = bearing(
    coordinates[coordinates.length - 2],
    coordinates[coordinates.length - 1],
  );
  const firstPoint = destination(
    coordinates[0],
    distanceDegrees,
    firstBearing,
    unitsDegrees,
  );
  const lastPoint = destination(
    coordinates[coordinates.length - 1],
    distanceDegrees,
    lastBearing,
    unitsDegrees,
  );
  return lineString([
    firstPoint.geometry.coordinates,
    ...coordinates.slice(1, -1),
    lastPoint.geometry.coordinates,
  ]);
};

/*
 * Split the given line segment at each intersection point with the given
 * polygon. Return an array of line segments, each of which lies inside the
 * given polygon.
 */
const splitLineSegmentAtIntersections = (
  lineSegment: LineSegment,
  polygon: Feature<Polygon | MultiPolygon>,
) => {
  const [startPoint, endPoint] = lineSegment;
  const startPointInPolygon = booleanPointInPolygon(startPoint, polygon);
  const endPointInPolygon = booleanPointInPolygon(endPoint, polygon);

  const intersections = lineIntersect(lineString(lineSegment), polygon);
  const intersectionDistancePairs = getSortedDistancePairsFromPoint(
    intersections.features,
    point(startPoint),
  );

  const lineSegments = <LineSegment[]>[];
  let enteringPoint = startPointInPolygon ? startPoint : null;
  intersectionDistancePairs.forEach(([, intersection]) => {
    if (enteringPoint == null) {
      enteringPoint = intersection.geometry.coordinates;
    } else {
      lineSegments.push([enteringPoint, intersection.geometry.coordinates]);
      enteringPoint = null;
    }
  });
  // Handle last point, if it is inside the polygon
  if (endPointInPolygon && enteringPoint) {
    lineSegments.push([enteringPoint, endPoint]);
  }
  return lineSegments;
};

/*
 * Converts the given line segments into a list of LineStrings, joining any line
 * segments that are connected at the same coordinates.
 */
const joinConnectedLineSegments = (lineSegments: LineSegment[]) => {
  const lines = <Feature<LineString>[]>[];
  let linePoints = <Position[]>[];
  lineSegments.forEach(([startPoint, endPoint]) => {
    if (!linePoints.length) {
      // Start first line
      linePoints.push(startPoint, endPoint);
    } else if (linePoints[linePoints.length - 1] === startPoint) {
      // Connected to current line, add end point to line
      linePoints.push(endPoint);
    } else {
      // Finalize current line
      lines.push(lineString(linePoints));
      // Reinitialize current line with next points
      linePoints = [startPoint, endPoint];
    }
  });
  // Handle last line
  if (linePoints.length) {
    lines.push(lineString(linePoints));
  }
  return lines;
};

/*
 * Split the given line at each intersection point with the given polygon.
 * Return an array of lines, each of which lies inside the given polygon.
 */
export const splitLineAtIntersections = (
  line: Feature<LineString>,
  polygon: Feature<Polygon | MultiPolygon>,
) => {
  // Split line string with multiple points into individual line segments
  const lineSegments = splitLineIntoSegments(line)
    // Split each segment into zero or more line segments inside the polygon
    .flatMap((lineSegment) =>
      splitLineSegmentAtIntersections(lineSegment, polygon),
    );
  // Rejoin line segments that are connected
  return joinConnectedLineSegments(lineSegments);
};
